import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import ModifySlotTimeRange from './ModifySlotTimeRange';
import styled from 'styled-components';
import moment from 'moment';
import { showConfirmation } from '../ConfirmationDialogService';
import { toMoment } from './ModifySlotContainer';
import { CHANGE_TIMING } from '../queries';
import { useMutation } from 'react-apollo';
import SlotCancelationReasons from './SlotCancelationReasons';
import { displayError, displaySuccess } from '../utilities';
import ModifySlotAffectedBookingsToolBar from './ModifySlotAffectedBookingsToolBar';

const SettingsContainer = styled.div`
  padding: 1rem;
  border: 1px solid #cccccc;
  border-radius: 1rem;
`;

const MainContainer = styled.div`
  & > * {
    margin-bottom: 1rem;
  }
`;

const OkCancel = styled.div`
  display: flex;
  justify-content: flex-end;
  & > button {
    margin-left: 1rem;
  }
`;

const Blue = styled.span`
  color: blue;
`;

const CancelationReason = styled.div`
  display: flex;
  align-items: center;
  & > * {
    margin-right: 1rem;
  }
`;

const ModifySlotChangeTiming = ({ onCancel, date, alteredSchedule }) => {
  const {
    start,
    end,
    slotPrototypeId,
    // isAltered,
    alteredStart,
    alteredEnd
    // isAdhoc,
    // hospitalName,
    // isTatkalEnabled,
    // isBlockAll,
    // userName,
    // updatedAt
  } = alteredSchedule;
  const [changeTimingObj, setChangeTimingObj] = useState({
    startTime: null,
    endTime: null,
    leaveExcessBookings: true,
    reason: null
  });
  const dateM = moment(date);
  const actualStartTime = alteredStart
    ? toMoment(alteredStart, dateM)
    : toMoment(start, dateM);
  const actualEndTime = alteredEnd
    ? toMoment(alteredEnd, dateM)
    : toMoment(end, dateM);
  const { startTime, endTime, leaveExcessBookings, reason } = changeTimingObj;

  let newStartTime = startTime || actualStartTime;
  newStartTime = moment().isAfter(newStartTime) ? moment() : newStartTime;
  let newEndTime = endTime || actualEndTime;
  newEndTime = newStartTime.isAfter(newEndTime)
    ? moment(newStartTime).add(15, 'minutes')
    : newEndTime;

  const [changeTiming, { loading }] = useMutation(CHANGE_TIMING, {
    variables: {
      input: {
        slotPrototypeId,
        newStartTime: newStartTime.toDate(),
        newEndTime: newEndTime.toDate(),
        keepOrphans: leaveExcessBookings,
        reason
      }
    },
    onCompleted: () => {
      displaySuccess('Schedule timing changed successfully');
      onCancel();
    },
    onError: e => displayError(e),
    refetchQueries: () => ['SEARCH_DOCTORS_EXTENDED']
  });
  const setTimeObjAttr = (attName, attVal) =>
    setChangeTimingObj({ ...changeTimingObj, [attName]: attVal });

  const origStartStr = actualStartTime.format('h:mm A');
  const origEndStr = actualEndTime.format('h:mm A');

  const newStartTimeStr = newStartTime.format('h:mm A');
  const newEndTimeStr = newEndTime.format('h:mm A');

  const okButtonColor = leaveExcessBookings ? 'primary' : 'danger';

  const okButtonText = `Change timing, move bookings, ${
    leaveExcessBookings ? 'leave excess' : 'cancel excess'
  }`;

  const onOk = () => {
    return showConfirmation(
      'Check Configuration',
      <ul style={{ textAlign: 'left' }}>
        <li>
          <strong>THIS ACTION WILL MOVE BOOKINGS</strong>
        </li>
        <li>This action will send new timing to affected patients</li>
        <li>
          Timing changes from {origStartStr} - {origEndStr} to{' '}
          <strong>
            {newStartTimeStr} - {newEndTimeStr}
          </strong>
        </li>
        <li>
          {leaveExcessBookings
            ? 'Unadjusted bookings will be left untouched'
            : 'Unadjusted bookings will be cancelled'}
        </li>
      </ul>,
      () => changeTiming()
    );
  };

  const disableOkButton = loading || !reason || (!startTime && !endTime);

  return (
    <MainContainer>
      <h4>
        Change Timing on <Blue>{dateM.format('ddd, DD MMM YYYY')}</Blue>
      </h4>
      <div>
        <strong>Existing timing:</strong>{' '}
        <span>
          {origStartStr} - {origEndStr}
        </span>
      </div>
      <div>
        <span>
          <strong>New Timing:</strong>
        </span>
        <ModifySlotTimeRange
          startTime={newStartTime}
          endTime={newEndTime}
          setEndTime={t => setTimeObjAttr('endTime', t)}
          setStartTime={t => setTimeObjAttr('startTime', t)}
        />
      </div>

      <CancelationReason>
        <div>Reason</div>
        <div>
          <SlotCancelationReasons onChange={r => setTimeObjAttr('reason', r)} />
        </div>
        {!reason && <span className="error">Required</span>}
      </CancelationReason>
      <SettingsContainer>
        <h4>What to do with bookings if there are more bookings than slots?</h4>
        <div>
          <input
            type="radio"
            checked={leaveExcessBookings}
            onChange={e =>
              setTimeObjAttr('leaveExcessBookings', e.currentTarget.checked)
            }
            id="leaveExcess"
          />{' '}
          <label htmlFor="leaveExcess">Leave excess bookings orphan</label>
        </div>
        <div>
          <input
            type="radio"
            checked={!leaveExcessBookings}
            onChange={e =>
              setTimeObjAttr('leaveExcessBookings', !e.currentTarget.checked)
            }
            id="cancelExcess"
          />{' '}
          <label htmlFor="cancelExcess">Cancel excess bookings</label>
        </div>
      </SettingsContainer>

      <ModifySlotAffectedBookingsToolBar
        modification="changeTiming"
        slotPrototypeId={slotPrototypeId}
        date={date}
        newStartTime={newStartTime.toDate()}
        newEndTime={newEndTime.toDate()}
        leaveOrphans={leaveExcessBookings}
        enabled={!disableOkButton}
      />
      <OkCancel>
        <Button onClick={onCancel}>Cancel</Button>
        <Button disabled={disableOkButton} color={okButtonColor} onClick={onOk}>
          {okButtonText}
        </Button>
      </OkCancel>
    </MainContainer>
  );
};

ModifySlotChangeTiming.propTypes = {
  alteredSchedule: PropTypes.objectOf(
    PropTypes.shape({
      start: PropTypes.string.isRequired,
      end: PropTypes.string.isRequired,
      slotPrototypeId: PropTypes.number.isRequired,
      isAltered: PropTypes.bool.isRequired,
      alteredStart: PropTypes.instanceOf(Date),
      alteredEnd: PropTypes.instanceOf(Date),
      isAdhoc: PropTypes.bool.isRequired,
      hospitalName: PropTypes.string,
      isTatkalEnabled: PropTypes.bool.isRequired,
      isBlockAll: PropTypes.bool
    })
  ).isRequired,
  onCancel: PropTypes.func.isRequired,
  date: PropTypes.instanceOf(Date).isRequired
};

export default ModifySlotChangeTiming;
